import { useState, useEffect } from "react";

const VISIT_KEY = "carelogic_visit_count";
const DISMISSED_KEY = "carelogic_install_prompt_dismissed_at";
const SESSION_KEY = "carelogic_visit_counted";
const DISMISS_DAYS = 30;

function isStandalone() {
  return (
    ("standalone" in window.navigator && (window.navigator as any).standalone === true) ||
    window.matchMedia("(display-mode: standalone)").matches
  );
}

function isIosSafari() {
  const ua = window.navigator.userAgent;
  return (
    (/iphone|ipad|ipod/i.test(ua)) &&
    !(window.navigator as any).standalone
  );
}

function isMobile() {
  return (
    window.screen.width <= 768 ||
    window.matchMedia("(max-width: 768px)").matches ||
    ("ontouchstart" in window)
  );
}

function wasDismissedRecently() {
  const ts = localStorage.getItem(DISMISSED_KEY);
  if (!ts) return false;
  const elapsed = Date.now() - parseInt(ts, 10);
  return elapsed < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

function getVisitCount(): number {
  return parseInt(localStorage.getItem(VISIT_KEY) || "0", 10);
}

function incrementVisitCount() {
  if (sessionStorage.getItem(SESSION_KEY)) return;
  const count = getVisitCount() + 1;
  localStorage.setItem(VISIT_KEY, String(count));
  sessionStorage.setItem(SESSION_KEY, "1");
}

export default function PwaInstallBanner() {
  const [visible, setVisible] = useState(false);
  const [isIosDevice, setIsIosDevice] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

  useEffect(() => {
    incrementVisitCount();

    if (!isMobile() || isStandalone() || wasDismissedRecently()) return;
    if (getVisitCount() < 2) return;

    const ios = isIosSafari();
    setIsIosDevice(ios);

    if (ios) {
      setVisible(true);
    } else {
      const handler = (e: Event) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setVisible(true);
      };
      window.addEventListener("beforeinstallprompt", handler);
      return () => window.removeEventListener("beforeinstallprompt", handler);
    }
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    }
    dismiss();
  };

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: "calc(64px + env(safe-area-inset-bottom) + 12px)",
        left: "50%",
        transform: "translateX(-50%)",
        width: "calc(100% - 32px)",
        maxWidth: 688,
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
        borderRadius: 16,
        boxShadow: "var(--shadow-md)",
        padding: "14px 16px",
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        zIndex: 45,
      }}
    >
      {/* Icon */}
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: "var(--color-primary)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2v13M7 9l5 6 5-6" />
          <path d="M3 19h18" />
        </svg>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "var(--color-text)" }}>
          Add CareLogic to your home screen for faster access
        </p>
        {isIosDevice ? (
          <div>
            <p style={{ margin: "4px 0 8px", fontSize: 12, color: "var(--color-text-muted)", lineHeight: 1.4 }}>
              Tap the{" "}
              <svg
                style={{ display: "inline", verticalAlign: "middle", marginBottom: 2 }}
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                <polyline points="16 6 12 2 8 6" />
                <line x1="12" y1="2" x2="12" y2="15" />
              </svg>{" "}
              <strong>share icon</strong> in Safari, then <strong>Add to Home Screen</strong>.
            </p>
            <button
              onClick={dismiss}
              style={{
                padding: "6px 14px",
                background: "var(--color-primary)",
                color: "var(--color-on-primary)",
                border: "none",
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Got it
            </button>
          </div>
        ) : (
          <button
            onClick={handleInstall}
            style={{
              marginTop: 8,
              padding: "6px 14px",
              background: "var(--color-primary)",
              color: "var(--color-on-primary)",
              border: "none",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Add to Home Screen
          </button>
        )}
      </div>

      {/* Dismiss */}
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: 4,
          color: "var(--color-text-muted)",
          flexShrink: 0,
          lineHeight: 1,
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}
